import { CarInsuranceApi } from '../../api/CarInsuranceApi';
import {
  CustomerDetailsDto,
  MarketingPreferencesDto,
  MarketingPreferencesResponse,
  RetrieveQuoteRequestType
} from '../../api/Models/InsuranceJourney';
import {
  AboutYouModel,
  AdditionalDriverDropDowns,
  AddressDetailsDropDowns,
  CarInsuranceVehicleModel,
  CoverDetailsDropDowns,
  DealerHomepageOptions,
  DrivingHistoryApiResponseModel,
  DropdownData,
  InsuranceJourneyState,
  KeyValuePair
} from '../../components/common/CommonTypes';
import { PagesWithDropdown } from '../../components/common/Constants';
import { populateSelectOptions } from '../../components/common/HelperMethods';
import { InsuranceVehicleVendor } from '../../consts/Common';

const useCarInsuranceController = () => {
  return {
    getNewQuote: async (
      brand: string,
      dealerCode: string,
      token: string,
      callBackOnSuccess: (quote: InsuranceJourneyState) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();

      const getQuoteSuccess = (quote: InsuranceJourneyState) => {
        quote.vehicleInfo.manufacturer = brand.toUpperCase();
        if (dealerCode) {
          quote.vehicleInfo.vendor = InsuranceVehicleVendor.FranchiseDealer;
          quote.vehicleInfo.dealer = dealerCode;
        }
        callBackOnSuccess(quote);
      };

      await new CarInsuranceApi().GetQuoteAsync(
        token,
        getQuoteSuccess,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    retrieveQuote: async (
      payload: RetrieveQuoteRequestType,
      token: string,
      callBackOnSuccess: (quote: InsuranceJourneyState) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();

      await new CarInsuranceApi().RetrieveQuoteAsync(
        payload,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    saveQuote: async (
      quote: InsuranceJourneyState,
      token: string,
      callBackOnSuccess: (quote: InsuranceJourneyState) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();

      await new CarInsuranceApi().SaveQuoteAsync(
        quote,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    getAboutYouPageDropdownValues: async (
      token: string,
      callBackOnSuccess: (aboutYouPageDropDownValues: DropdownData[]) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const getDropdownData = (aboutYouList: AboutYouModel) => {
        const dropdownData: DropdownData[] = [
          {
            page: PagesWithDropdown.AboutYouPage,
            controlName: 'maritalStatus',
            values: populateSelectOptions(aboutYouList.maritalStatuses)
          },
          {
            page: PagesWithDropdown.AboutYouPage,
            controlName: 'title',
            values: populateSelectOptions(aboutYouList.titles)
          },
          {
            page: PagesWithDropdown.AboutYouPage,
            controlName: 'genders',
            values: populateSelectOptions(aboutYouList.genders)
          },
          {
            page: PagesWithDropdown.AboutYouPage,
            controlName: 'employmentStatus',
            values: populateSelectOptions(aboutYouList.employmentStatuses)
          }
        ];

        return callBackOnSuccess(dropdownData);
      };

      const abortController = new AbortController();

      await new CarInsuranceApi().GetAboutYouDropDownListsAsync(
        token,
        getDropdownData,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    getAdditionalDriverPageDropdownValues: async (
      token: string,
      callBackOnSuccess: (
        additionalDriverDropdownValues: AdditionalDriverDropDowns
      ) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();
      await new CarInsuranceApi().GetDropDownValuesForAdditionalDriverAsync(
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );
      return () => {
        abortController.abort();
      };
    },

    getDrivingHistoryPageDropdownValues: async (
      dateOfBirth: Date | undefined,
      token: string,
      callBackOnSuccess: (
        dropdownValues: DrivingHistoryApiResponseModel
      ) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForDrivingHistoryAsync(
        dateOfBirth,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getVehicleDetailsPageDropdownValues: async (
      tiaProductCode: string,
      token: string,
      callBackOnSuccess: (vehciledetails: CarInsuranceVehicleModel) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();
      await new CarInsuranceApi().GetDropDownValuesForVehicleDetailsAsync(
        tiaProductCode,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getDropDownValuesForVehicleSpecifications: async (
      make: string,
      model: string,
      fuelType: string,
      transmissionType: string,
      noOfDoors: string,
      token: string,
      callBackOnSuccess: (vehicleMakes: KeyValuePair[]) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForVehicleSpecificationsAsync(
        make,
        model,
        fuelType,
        transmissionType,
        noOfDoors,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getDropdownValuesForVehicleMake: async (
      token: string,
      callBackOnSuccess: (vehicleMakes: KeyValuePair[]) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForVehicleMakeAsync(
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getDropdownValuesForVehicleModel: async (
      make: string,
      token: string,
      callBackOnSuccess: (vehicleMakes: KeyValuePair[]) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForVehicleModelsAsync(
        make,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getTrackingDevicesBySearch: async (
      searchText: string,
      token: string,
      successFunction: (trackingDevices: KeyValuePair[]) => void,
      failureFunction: (err: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetTrackingDevicesBySearchAsync(
        searchText,
        token,
        successFunction,
        failureFunction,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getCoverDetailsPageDropdownValues: async (
      token: string,
      callBackOnSuccess: (coverDetailsList: CoverDetailsDropDowns) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();
      await new CarInsuranceApi().GetDropDownValuesForCoverDetailsAsync(
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getAddressDetailsPageDropdownValues: async (
      token: string,
      callBackOnSuccess: (AddressDetailsList: AddressDetailsDropDowns) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForAddressDetailsAsync(
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getYourQuotePageDropdownValues: async (
      token: string,
      callBackOnSuccess: (dropdownValues: KeyValuePair[]) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetDropDownValuesForVoluntaryExcessAsync(
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getQuoteDocumentById: async (
      documentId: number,
      token: string,
      callBackOnSuccess: (documentContent: string) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetQuoteDocument(
        documentId,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    generateQuoteDocument: async (
      quoteId: number,
      documentType: string,
      token: string,
      callBackOnSuccess: (documentContent: string) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GenerateQuoteDocument(
        quoteId,
        documentType,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    },

    getMarketingPreferences: async (
      payload: CustomerDetailsDto,
      token: string,
      callBackOnSuccess: (
        marketingPreferences: MarketingPreferencesResponse
      ) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();

      await new CarInsuranceApi().GetMarketingPreferencesAsync(
        payload,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    updateMarketingPreferences: async (
      payload: MarketingPreferencesDto,
      token: string,
      callBackOnSuccess: (flag: boolean) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();

      await new CarInsuranceApi().UpdateMarketingPreferences(
        payload,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    },

    getDealerHomepageOptionsAsync: async (
      dealerCode: string,
      tiaProductCode: string,
      token: string,
      callBackOnSuccess: (homePageOptions: DealerHomepageOptions) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const abortController = new AbortController();
      await new CarInsuranceApi().GetDealerHomepageOptionsAsync(
        dealerCode,
        tiaProductCode,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        abortController.signal
      );
      return () => {
        abortController.abort();
      };
    },

    getPolicyDocument: async (
      scheme: string,
      tiaProductCode: string,
      documentType: string,
      token: string,
      callBackOnSuccess: (documentContent: string) => void,
      callBackOnFailure: (error: string) => void
    ) => {
      const controller = new AbortController();

      await new CarInsuranceApi().GetPolicyDocument(
        scheme,
        tiaProductCode,
        documentType,
        token,
        callBackOnSuccess,
        callBackOnFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    }
  };
};

export default useCarInsuranceController;
